import { useMemo, useState } from "react";
import { SettingsSection } from "./_legacy_section";
import { DateRangePicker } from "@/components/DateRangePicker";
import type { RangeMode, RangePreset } from "@/lib/date-range";
import {
  type ChangeSource,
  type PlatformChangeRow,
  usePlatformChanges,
} from "@/lib/api-hooks";

/** Изменения платформы — админская страница.
 *
 *  Показывает КАЖДОЕ изменение из трёх источников сразу: сработавшие
 *  правила, очередь записи к Apple и синхронные ручные пути. До
 *  объединения страница читала один только `audit_log` и потому не
 *  показывала ни одной правки, сделанной правилом: движок работает в
 *  воркере и в этот журнал не пишет вовсе.
 *
 *  ★ Период обязателен и всегда задан. Прежняя версия открывалась
 *  «последними ста без дат» — по такой странице нельзя ответить на
 *  единственный вопрос, ради которого её открывают: что менялось
 *  тогда-то.
 *
 *  Листание курсорное, а не по смещению: источники дописываются
 *  постоянно, и `OFFSET` по растущему набору пропускает одни строки и
 *  повторяет другие. Отсюда «Назад/Вперёд» вместо номеров страниц —
 *  общего числа строк у курсорного листания нет.
 */

const PAGE_SIZE = 100;

const SOURCE_LABEL: Record<ChangeSource, string> = {
  rule: "Правило",
  queue: "Очередь",
  manual: "Вручную",
};

const OUTCOME_LABEL: Record<string, { text: string; tone: string }> = {
  applied: { text: "применено", tone: "var(--c-text-3)" },
  synced: { text: "принято Apple", tone: "var(--c-text-3)" },
  recorded: { text: "записано", tone: "var(--c-text-3)" },
  error: { text: "Apple отказала", tone: "var(--c-neg-600)" },
};

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function AuditLogPage() {
  const [range, setRange] = useState<{ from: Date; to: Date }>(() => {
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - 7);
    return { from, to };
  });
  const [preset, setPreset] = useState<RangePreset>("7d");
  const [mode, setMode] = useState<RangeMode>("rolling");
  const [sources, setSources] = useState<ChangeSource[]>([]);

  // Курсоры пройденных страниц. Ноль — первая страница; «Назад» просто
  // возвращает на уже посещённый курсор, поэтому лишних запросов нет.
  const [stack, setStack] = useState<(string | null)[]>([null]);
  const [idx, setIdx] = useState(0);

  const dateFrom = useMemo(() => ymd(range.from), [range.from]);
  const dateTo = useMemo(() => ymd(range.to), [range.to]);

  const query = usePlatformChanges({
    dateFrom,
    dateTo,
    sources,
    limit: PAGE_SIZE,
    cursor: stack[idx],
  });

  // Любая смена фильтра обнуляет листание: курсор описывает позицию
  // внутри КОНКРЕТНОГО набора, и переносить его в другой набор нельзя.
  const resetPaging = () => {
    setStack([null]);
    setIdx(0);
  };

  const toggleSource = (s: ChangeSource) => {
    setSources((cur) =>
      cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s],
    );
    resetPaging();
  };

  const rows = query.data?.rows ?? [];
  const nextCursor = query.data?.next_cursor ?? null;

  const goNext = () => {
    if (!nextCursor) return;
    setStack((s) => [...s.slice(0, idx + 1), nextCursor]);
    setIdx(idx + 1);
  };

  return (
    <SettingsSection
      title="Изменения платформы"
      description={
        "Каждое изменение за выбранный период: правки правил, ручные и " +
        "массовые. Для каждой строки — дата, сущность, поле «до → после» " +
        "и автор (правило поимённо либо пользователь)."
      }
    >
      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          {(["rule", "queue", "manual"] as ChangeSource[]).map((s) => (
            <button
              key={s}
              type="button"
              className={"btn sm" + (sources.includes(s) ? " active" : "")}
              aria-pressed={sources.includes(s)}
              onClick={() => toggleSource(s)}
              style={
                sources.includes(s)
                  ? {
                      background: "var(--c-accent-600)",
                      color: "#fff",
                      borderColor: "var(--c-accent-600)",
                    }
                  : undefined
              }
            >
              {SOURCE_LABEL[s]}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: "auto" }}>
          <DateRangePicker
            preset={preset}
            range={range}
            mode={mode}
            onChange={({ preset: p, range: r, mode: m }) => {
              setPreset(p);
              setRange(r);
              setMode(m);
              resetPaging();
            }}
          />
        </div>
      </div>

      <div
        className="muted"
        style={{ fontSize: 12, marginBottom: 8, fontVariantNumeric: "tabular-nums" }}
      >
        {query.isLoading
          ? "Загрузка…"
          : query.isError
          ? "Не удалось загрузить изменения."
          : rows.length === 0
          ? "За выбранный период изменений нет."
          : `Страница ${idx + 1} · строк: ${rows.length}`}
      </div>

      <div
        className="table-scroll"
        style={{
          border: "1px solid var(--c-border)",
          borderRadius: 10,
          background: "var(--c-surface)",
          overflowX: "auto",
        }}
      >
        <table className="data-table" style={{ width: "100%", fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ width: 150, whiteSpace: "nowrap" }}>Когда (UTC)</th>
              <th style={{ width: 90 }}>Источник</th>
              <th style={{ width: 250 }}>Сущность</th>
              <th style={{ width: 190 }}>Поле</th>
              <th style={{ width: 200 }}>Было → стало</th>
              <th>Автор</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <ChangeRow key={`${r.source}-${r.row_id}-${r.field ?? ""}`} r={r} />
            ))}
          </tbody>
        </table>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 12,
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <button
          className="btn sm"
          disabled={idx === 0 || query.isFetching}
          onClick={() => setIdx(Math.max(0, idx - 1))}
          type="button"
        >
          ← Назад
        </button>
        <button
          className="btn sm"
          disabled={!nextCursor || query.isFetching}
          onClick={goNext}
          type="button"
        >
          Вперёд →
        </button>
      </div>
    </SettingsSection>
  );
}

function ChangeRow({ r }: { r: PlatformChangeRow }) {
  const [open, setOpen] = useState(false);
  const outcome = OUTCOME_LABEL[r.outcome] ?? {
    text: r.outcome,
    tone: "var(--c-text-3)",
  };
  return (
    <>
      <tr>
        <td
          style={{
            fontFamily: "var(--font-mono)",
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }}
        >
          {fmtTs(r.ts)}
        </td>
        <td>
          <span style={{ fontSize: 11.5, color: "var(--c-text-2)" }}>
            {SOURCE_LABEL[r.source]}
          </span>
          <div style={{ fontSize: 10.5, color: outcome.tone }}>{outcome.text}</div>
        </td>
        <td>
          <EntityCell r={r} />
        </td>
        <td>
          <code style={{ fontSize: 11.5, color: "var(--c-text-2)" }}>
            {r.field ?? r.action}
          </code>
        </td>
        <td>
          <BeforeAfter r={r} />
        </td>
        <td>
          <div style={{ minWidth: 0 }}>
            <span style={{ color: "var(--c-text)" }}>{r.author.name}</span>
            <button
              type="button"
              onClick={() => setOpen((x) => !x)}
              style={{
                marginLeft: 8,
                fontSize: 11,
                background: "transparent",
                border: "none",
                color: "var(--c-accent-600)",
                cursor: "pointer",
                padding: "0 4px",
              }}
            >
              {open ? "скрыть" : "подробности"}
            </button>
          </div>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6}>
            <pre
              style={{
                margin: "4px 0 10px",
                fontSize: 11,
                background: "var(--c-surface-sunk)",
                border: "1px solid var(--c-border)",
                borderRadius: 6,
                padding: 8,
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                maxHeight: 280,
                overflow: "auto",
              }}
            >
              {JSON.stringify(
                { action: r.action, entity_id: r.entity_id, details: r.details },
                null,
                2,
              )}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

/** Имя сущности плюс путь к ней. Голый идентификатор (#2247256167)
 *  прочитать нельзя, поэтому имя из зеркала — основной текст, а
 *  идентификатор остаётся только там, где имени не нашлось. */
function EntityCell({ r }: { r: PlatformChangeRow }) {
  const trail = [r.campaign_name, r.ad_group_name].filter(Boolean).join(" / ");
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ color: "var(--c-text)" }}>
        {r.entity_name ?? (r.entity_id ? `#${r.entity_id}` : "—")}
      </div>
      {trail && (
        <div style={{ fontSize: 11, color: "var(--c-text-3)" }}>{trail}</div>
      )}
    </div>
  );
}

/** «Было → стало».
 *
 *  ★ У точечной ручной правки значения «было» в базе НЕТ: зеркало
 *  переписывается на месте в момент постановки заявки, и прежнее
 *  значение теряется тогда же. Здесь рисуется прочерк — подставить
 *  нынешнее значение зеркала и выдать его за прошлое означало бы
 *  показать число, которого в тот момент не было. */
function BeforeAfter({ r }: { r: PlatformChangeRow }) {
  if (r.before == null && r.after == null) {
    return <span className="muted">—</span>;
  }
  return (
    <span style={{ fontVariantNumeric: "tabular-nums" }}>
      <span style={{ color: "var(--c-text-3)" }}>{r.before ?? "—"}</span>
      <span style={{ margin: "0 6px", color: "var(--c-text-3)" }}>→</span>
      <span style={{ color: "var(--c-text)" }}>{r.after ?? "—"}</span>
    </span>
  );
}

function fmtTs(iso: string): string {
  // Явно в UTC — период тоже задан в UTC, и перевод в местное время
  // рассогласовал бы строку с фильтром, по которому её нашли.
  try {
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, "0");
    return (
      `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ` +
      `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`
    );
  } catch {
    return iso;
  }
}
